import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { apiList } from "../services/list";
import { ContentType, HomepageSectionType, Orientation, PurchaseType, } from "../types/global";
import { getPlayersSettings, useGroupData, useGroupLook, } from "../stores/useGroupStore";
import { useAutoplay } from "../hooks/useAutoplay";
import { ROUTES } from "../constants/routes";
import { useTranslation } from "react-i18next";
import ContentWatchSectionSwiper from "../components/watch-video/ContentWatchSectionSwiper";
import NavbarContext from "../components/navbar/NavbarContext";
import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { apiContent } from "../services/content";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, } from "@/components/ui/alert-dialog";
import VideoPlayer from "../components/player/VideoPlayer";
import VideoInfo from "../components/watch-video/VideoInfo";
import AdBlockModal from "../components/adblock/AdBlockModal";
import { VideoPlayerConfig } from "../components/player/VideoPlayerType";
import { checkContentLockedForUser, getContentPurchaseTypes, } from "../lib/utils";
import PurchaseChoiceDialog from "../components/helpers/PurchaseChoiceDialog";
import GenericButton from "../components/helpers/GenericButton";
import useTailwindBreakpoint from "../hooks/useTailwindBreakpoint";
const rel = Number(new URLSearchParams(window.location.search).get("rel"));
const cat = Number(new URLSearchParams(window.location.search).get("cat"));
const prel = Number(new URLSearchParams(window.location.search).get("prel"));
function VideoList({ content_data, player_settings, videoEnded, }) {
    const groupData = useGroupData();
    const groupLook = useGroupLook();
    const isLg = useTailwindBreakpoint("lg");
    const content_type = content_data.type;
    const isOnLiveContent = content_type == ContentType.Livestream ||
        content_type == ContentType.Fastchannels ||
        cat == 1;
    const content_id = content_data.info[0].id;
    let content_father_id = 0;
    let content_father_type = 0;
    if (cat == HomepageSectionType.Custom) {
        content_father_id = prel
            ? prel
            : content_data.info[0].folders_id
                ? content_data.info[0].folders_id
                : 0;
        content_father_type = prel ? 1 : 0;
    }
    const { isFetchingNextPage, hasNextPage, data, isPending, fetchNextPage } = useInfiniteQuery({
        queryKey: [
            "content-list",
            content_id,
            content_type,
            content_father_id,
            content_father_type,
        ],
        queryFn: ({ pageParam }) => {
            return apiList.list.contentList({
                current_page: pageParam,
                content_type: content_type,
                parent_id: content_father_id,
                parent_type: content_father_type,
            });
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, _, lastPageParam) => lastPage.content_list &&
            lastPage.content_list?.length > 0 &&
            lastPage.page != lastPage.total_count_page
            ? lastPageParam + 1
            : undefined,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        refetchOnMount: true,
    });
    // Una pagina senza content_list non deve lasciare buchi (undefined) nella lista
    const actualData = React.useMemo(() => (data?.pages.flatMap((page) => page.content_list ?? []) ??
        []), [data]);
    const currentContentIndex = actualData?.findIndex((content) => content.id == content_id);
    const [autoplay] = useAutoplay();
    //siamo nella vista landscape
    const landscape = player_settings?.landscape;
    //carosello attivo solo nella visone social
    const needAutoplay = Boolean(landscape?.land_standard_status);
    const section = React.useMemo(() => {
        if (!actualData)
            return null;
        return {
            content_list: actualData,
            type: cat,
            title: data?.pages[0]?.title ?? "Contenuti",
            cover_orientation: 0,
        };
    }, [actualData]);
    const [nextContentPurchaseTypes, setNextContentPurchaseTypes] = React.useState(null);
    React.useEffect(() => {
        if (!videoEnded ||
            !autoplay ||
            isFetchingNextPage ||
            actualData.length === 0) {
            return;
        }
        const isLastLoadedContent = currentContentIndex === -1 ||
            currentContentIndex + 1 > actualData.length - 1;
        // Il contenuto successivo puo' essere nella pagina dopo: la carico prima di decidere
        if (isLastLoadedContent && hasNextPage) {
            fetchNextPage();
            return;
        }
        // Finita la lista (o contenuto corrente non presente) si riparte dal primo
        const nextContent = isLastLoadedContent
            ? actualData[0]
            : actualData[currentContentIndex + 1];
        if (!nextContent) {
            return;
        }
        const contentInfo = typeof nextContent.info === "string"
            ? JSON.parse(nextContent.info)
            : nextContent.info;
        const locked = checkContentLockedForUser({ id: nextContent.id, info: contentInfo }, groupData.data?.user_data, groupLook.look?.model_data);
        const purchaseTypes = getContentPurchaseTypes({ id: nextContent.id, locked: locked, info: contentInfo }, groupData.data?.user_data, groupLook.look?.model_data);
        if (purchaseTypes[PurchaseType.Buy] ||
            purchaseTypes[PurchaseType.Rent] ||
            purchaseTypes[PurchaseType.Subscribe]) {
            setNextContentPurchaseTypes(purchaseTypes);
        }
        else {
            window.location.href = ROUTES.WATCH_VIDEO(nextContent.id, cat, prel ? 1 : 0, prel ? prel : undefined);
        }
    }, [
        videoEnded,
        autoplay,
        actualData,
        currentContentIndex,
        isFetchingNextPage,
        hasNextPage,
    ]);
    const handleReachEnd = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };
    if (!section || isPending) {
        return _jsx(Skeleton, { className: "skeleton-custom h-full w-full rounded-xl" });
    }
    const swiperOrientation = !isLg || isOnLiveContent ? Orientation.Landscape : Orientation.Portrait;
    return (_jsxs(_Fragment, { children: [nextContentPurchaseTypes != null && (_jsx(PurchaseChoiceDialog, { buyUrl: nextContentPurchaseTypes[PurchaseType.Buy], rentUrl: nextContentPurchaseTypes[PurchaseType.Rent], subscribeUrl: nextContentPurchaseTypes[PurchaseType.Subscribe] })), _jsx(ContentWatchSectionSwiper, { data: section, selectedId: content_id, showAutoplay: needAutoplay, swiperOrientation: swiperOrientation, onReachEnd: handleReachEnd })] }));
}
export default function WatchVideo() {
    const { t } = useTranslation();
    const isLg = useTailwindBreakpoint("lg");
    const groupLook = useGroupLook();
    const groupData = useGroupData();
    const [videoEnded, setVideoEnded] = React.useState(false);
    const [adBlockDetected, setAdBlockDetected] = React.useState(false);
    const playerSetting = getPlayersSettings();
    // Il return null per playerSetting mancante sta dopo tutti gli hook: se lo metto qui
    // React vede un numero di hook diverso tra un render e l'altro appena il look arriva
    const { isPending, data, error } = useQuery({
        queryKey: ["content-info", rel],
        queryFn: () => apiContent.content.contentInfo({
            content_id: rel,
            content_type: ContentType.AllVideoType, //ContentType.Video
        }),
        //Lorenzo S. 21/08/2026: fa 2 retry solo se l'errore e' di tipo 407 (data in construction)
        retry: (failureCount, error) => {
            if (error instanceof AxiosError && error.response?.status === 407) {
                return failureCount < 2;
            }
            return false;
        },
        retryDelay: 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        refetchOnMount: true,
        enabled: !!rel,
    });
    const contentUnderPurchase = data?.code == 409;
    const contentWatchData = data;
    const contentPurchaseData = data;
    const contentPurchaseTypes = React.useMemo(() => {
        if (!(contentUnderPurchase && contentPurchaseData)) {
            return {
                [PurchaseType.Buy]: null,
                [PurchaseType.Rent]: null,
                [PurchaseType.Subscribe]: null,
            };
        }
        const contentPurchaseDataInfo = typeof contentPurchaseData.content === "string"
            ? JSON.parse(contentPurchaseData.content)
            : contentPurchaseData.content;
        return getContentPurchaseTypes({ id: rel, locked: true, info: contentPurchaseDataInfo }, groupData.data?.user_data, groupLook.look?.model_data);
    }, [
        contentUnderPurchase,
        contentPurchaseData,
        groupData.data?.user_data,
        groupLook.look?.model_data,
    ]);
    const content_type = contentWatchData?.content?.type;
    const isOnLiveContent = content_type == ContentType.Livestream ||
        content_type == ContentType.Fastchannels ||
        cat == 1;
    const videoPlayerInfo = React.useMemo(() => {
        const contentPlayInfo = contentWatchData?.content?.info?.[0]?.info;
        if (!contentPlayInfo) {
            return null;
        }
        try {
            const playInfo = JSON.parse(contentPlayInfo);
            // L'API restituisce la source_url con un "\n" finale: il browser lo ignora quando carica
            // il video, ma il confronto con player.currentSrc() no e il player si ricaricava a ogni render
            if (typeof playInfo.source_url === "string") {
                playInfo.source_url = playInfo.source_url.trim();
            }
            return playInfo;
        }
        catch {
            return null;
        }
    }, [data]);
    /**
     *
     * Metadati visualizzati sotto al player
     */
    const videoPlayerMetadata = React.useMemo(() => {
        const contentPlayMetadata = contentWatchData?.content?.info?.[0];
        if (!contentPlayMetadata) {
            return null;
        }
        const flags = typeof contentPlayMetadata.flags === "string"
            ? JSON.parse(contentPlayMetadata.flags)
            : contentPlayMetadata.flags;
        const image = typeof contentPlayMetadata.image === "string"
            ? JSON.parse(contentPlayMetadata.image)
            : contentPlayMetadata.image;
        try {
            return {
                ...contentPlayMetadata,
                image: image,
                flags: { metadata: flags.metadatas, tags: flags.tags },
            };
        }
        catch {
            return null;
        }
    }, [data]);
    //tipo di configurazioneper il player
    const configType = isOnLiveContent
        ? VideoPlayerConfig.Live
        : VideoPlayerConfig.Default;
    const enableLiveSeekbar = Number(playerSetting?.enable_live_seekbar);
    // Le options devono restare lo stesso oggetto tra un render e l'altro: il VideoPlayer
    // le usa come dipendenza e ogni nuovo oggetto gli faceva rieseguire l'effect del player
    const options = React.useMemo(() => {
        if (configType == VideoPlayerConfig.Live) {
            return {
                liveui: !!enableLiveSeekbar,
                ...(enableLiveSeekbar && {
                    liveTracker: {
                        trackingThreshold: 5,
                    },
                }),
                ...(!isLg && {
                    playbackRates: [],
                    controlBar: {
                        currentTimeDisplay: false,
                        durationDisplay: false,
                        timeDivider: false,
                        remainingTimeDisplay: false,
                    },
                    speed: { default: 1 },
                }),
                ...(!enableLiveSeekbar &&
                    isLg && {
                    controlBar: {
                        currentTimeDisplay: false,
                        durationDisplay: false,
                        timeDivider: false,
                        remainingTimeDisplay: false,
                    },
                }),
            };
        }
        return {
            ...(isLg && {
                controlBar: {
                    skipButtons: {
                        backward: 5,
                        forward: 5,
                    },
                    playbackRates: [0.5, 1, 2],
                    children: [
                        "playToggle",
                        "skipBackward",
                        "skipForward",
                        "volumePanel",
                        "progressControl",
                        "currentTimeDisplay",
                        "timeDivider",
                        "durationDisplay",
                        "customControlSpacer",
                        "playbackRateMenuButton",
                        "subsCapsButton",
                        "pictureInPictureToggle",
                        "fullscreenToggle",
                    ],
                },
            }),
            ...(!isLg && {
                controlBar: {
                    children: [
                        "playToggle",
                        "volumePanel",
                        "progressControl",
                        "currentTimeDisplay",
                        "timeDivider",
                        "durationDisplay",
                        "customControlSpacer",
                        "subsCapsButton",
                        "pictureInPictureToggle",
                        "fullscreenToggle",
                    ],
                },
            }),
        };
    }, [configType, enableLiveSeekbar, isLg]);
    // Callback stabili per lo stesso motivo delle options
    const handleVideoEnded = React.useCallback(() => setVideoEnded(true), []);
    const handleAdBlockDetected = React.useCallback(() => setAdBlockDetected(true), []);
    if (!playerSetting)
        return null;
    //siamo nella vista landscape
    const landscape = playerSetting.landscape;
    const isCinemaMode = Boolean(Number(landscape.land_cinema_status)) || isOnLiveContent;
    const playerLayoutClass = `${isCinemaMode ? "lg:h-[75vh]" : "h-auto lg:rounded-xl"}`;
    const infoClass = `${isCinemaMode ? "lg:px-[20%] " : "lg:px-0"} px-4`;
    const playerClass = `relative mx-auto aspect-video w-full overflow-hidden ${playerLayoutClass}`;
    if (isPending) {
        return (_jsxs(_Fragment, { children: [_jsx(Skeleton, { className: `skeleton-custom ${playerClass} mt-16` }), _jsx("div", { className: infoClass, children: _jsxs("div", { className: `z-100 mt-4 py-1`, children: [_jsx(Skeleton, { className: "mb-2 h-6 w-2/5" }), _jsx(Skeleton, { className: "mb-1 h-5 w-2/5" }), _jsxs("div", { className: "mb-2 flex flex-wrap items-center gap-3", children: [_jsx(Skeleton, { className: "h-5 w-40" }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx(Skeleton, { className: "h-6 w-16 rounded-md" }), _jsx(Skeleton, { className: "h-6 w-20 rounded-md" }), _jsx(Skeleton, { className: "h-6 w-14 rounded-md" })] })] }), _jsxs("div", { className: "mb-5 h-20 rounded-xl bg-[#ECECEC] p-4 dark:bg-[#242637]", children: [_jsx(Skeleton, { className: "mb-2 h-4 w-full dark:bg-[#75788f]" }), _jsx(Skeleton, { className: "mb-2 h-4 w-11/12 dark:bg-[#75788f]" })] })] }) })] }));
    }
    if (error) {
        return (_jsx("div", { className: `${playerClass} bg-background`, children: _jsx(AlertDialog, { defaultOpen: true, children: _jsxs(AlertDialogContent, { children: [_jsxs(AlertDialogHeader, { children: [_jsx(AlertDialogTitle, { children: t("playback-error") }), _jsx(AlertDialogDescription, { children: error.response?.data.message })] }), _jsx(AlertDialogFooter, { children: _jsx(AlertDialogAction, { children: "OK" }) })] }) }) }));
    }
    //carosello attivo solo nella visone social o live
    const needCarousel = Boolean(Number(landscape?.land_standard_status)) && !isOnLiveContent;
    //classe per la visione social
    const viewSocialClass = `${needCarousel
        ? "mx-auto flex w-full max-w-[1800px] flex-col gap-6 lg:flex-row lg:px-6 pt-5"
        : "flex flex-col gap-4"}`;
    const homeButton = (_jsx(GenericButton, { variant: "home", size: "md", icon: false, groupColor: false, asChild: true, children: _jsx("a", { href: ROUTES.HOME() }) }));
    return (_jsx(NavbarContext, { className: "flex min-h-screen grow-0 flex-col", children: contentUnderPurchase ? (_jsx(PurchaseChoiceDialog, { buyUrl: contentPurchaseTypes[PurchaseType.Buy], rentUrl: contentPurchaseTypes[PurchaseType.Rent], subscribeUrl: contentPurchaseTypes[PurchaseType.Subscribe], closable: false, moreButtons: homeButton })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: viewSocialClass, children: [_jsx("div", { className: Boolean(Number(landscape.land_cinema_status))
                                ? "relative mx-auto w-full"
                                : "flex min-w-0 flex-1 justify-center lg:basis-[68%]", children: _jsxs("div", { className: "relative w-full", children: [adBlockDetected ? (_jsx("div", { className: `${playerClass} bg-black` })) : (_jsx(VideoPlayer, { landscape: landscape, onEnded: handleVideoEnded, onAdBlockDetected: handleAdBlockDetected, config: configType, rel: rel, data: data, videoPlayerInfo: videoPlayerInfo, videoPlayerMetadata: videoPlayerMetadata, playerClass: playerClass, options: options })), _jsx("div", { className: infoClass, children: _jsx(VideoInfo, { info: videoPlayerMetadata }) })] }) }), needCarousel && (_jsx("div", { className: `mb-4 flex h-64 w-full min-w-0 flex-col overflow-hidden px-5 lg:sticky lg:top-20 lg:mb-0 lg:h-[calc(100vh-6rem)] lg:basis-[32%] lg:px-0`, children: _jsx(VideoList, { content_data: contentWatchData?.content, videoEnded: videoEnded, player_settings: playerSetting }) })), isOnLiveContent && (_jsx("div", { className: `mb-4 w-full min-w-0 overflow-x-hidden px-5 py-5 lg:sticky lg:top-20 lg:mb-0 lg:h-[calc(100vh-6rem)] lg:basis-[32%]`, children: _jsx(VideoList, { content_data: contentWatchData?.content, videoEnded: videoEnded, player_settings: playerSetting }) }))] }), adBlockDetected && _jsx(AdBlockModal, {})] })) }));
}
